import { Section } from '@/components/containers/section';
import { useBranding } from '@/outlets/branding-outlet';
import { Button } from '@/components/button';
import { idVerificationDocsLink } from '@/util/urls';
import { CameraSvg } from '@/components/svgs/camera';
import { IdCardSvg } from '@/components/svgs/id-card';
import styles from './id-verification-section.module.css';
import { GetPersonalInfoResponse } from '@/api/api.schemas';
import clsx from 'clsx';
import { useTranslation } from '@/contexts/i18n-context';

const translationBase = 'visit.preScreenQuestions';

interface IdVerificationSectionProps {
  patient: GetPersonalInfoResponse;
}

export function IdVerificationSection({ patient }: IdVerificationSectionProps) {
  const { t } = useTranslation();
  const { hasIdVerification } = useBranding();

  if (!hasIdVerification) {
    return null;
  }

  const isIdVerified = patient.idvVerified || patient.surrogate?.verified;

  return (
    <Section className={styles.idVerificationSection}>
      <div className={styles.idVerificationItem}>
        <h2 className="h4">{t(`${translationBase}.idVerification.title`)}</h2>
        <span className={styles.helpText}>{t(`${translationBase}.idVerification.body`)}</span>
      </div>

      <div className={styles.idVerificationItem}>
        <h2 className={clsx('bold', 'body-1', styles.title)}>
          {t(
            `${translationBase}.idVerification.whatYouNeed${isIdVerified ? 'Reverification' : ''}`,
          )}
        </h2>
        <div className={styles.accessDescriptionContainer}>
          {!isIdVerified && (
            <div className={styles.accessDescription}>
              <IdCardSvg className={styles.icon} />
              <div className={styles.accessDescriptionTexts}>
                <span>{t(`${translationBase}.idVerification.idAccess`)}</span>
                <Button
                  variant="link"
                  as="a"
                  to={idVerificationDocsLink}
                  target="_blank"
                  size="M"
                  className={styles.link}
                >
                  {t(`${translationBase}.idVerification.link`)}
                </Button>
              </div>
            </div>
          )}

          <div className={styles.accessDescription}>
            <CameraSvg className={styles.icon} />
            <span className={clsx(isIdVerified && styles.accessDescriptionText)}>
              {t(
                `${translationBase}.idVerification.cameraAccess${isIdVerified ? 'Reverification' : ''}`,
              )}
            </span>
          </div>
        </div>
      </div>
    </Section>
  );
}
